import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';

/**
 * Hook for bulk importing pages to a project
 * Leverages the existing project assignment patterns from the Add to Project system
 */
export const useBulkPageImport = (targetProjectId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pageIds: string[]) => {
      if (pageIds.length === 0) {
        throw new Error('No pages selected for import');
      }

      console.log('[BulkPageImport] Importing pages to project:', {
        targetProjectId,
        pageCount: pageIds.length,
        pageIds
      });

      // Update all selected pages to assign them to the target project
      const { data, error } = await supabase
        .from('workbook_page_content')
        .update({ workbook_id: targetProjectId })
        .in('id', pageIds)
        .select('id, title, workbook_id');

      if (error) {
        console.error('[BulkPageImport] Error updating pages:', error);
        throw error;
      }

      console.log('[BulkPageImport] Successfully imported pages:', data);
      return data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      queryClient.invalidateQueries({ queryKey: ['project-pages', targetProjectId] });
      queryClient.invalidateQueries({ queryKey: ['project-navigation'] });
      queryClient.invalidateQueries({ queryKey: ['workbook-pages'] });
      
      // CRITICAL: Invalidate the infinite user pages sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-pages'] });
      
      // Trigger quest completion for page import (Step 6)
      QuestTriggers.pageImported();
      
      // Show success toast
      toast({
        title: "Pages Imported Successfully",
        description: `${data.length} page${data.length === 1 ? '' : 's'} imported to project.`,
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error('[BulkPageImport] Import failed:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import pages. Please try again.",
        variant: "destructive",
      });
    },
  });
}; 